/**
 * Karte „Schulträger“ in Einstellungen > Allgemein: Schulträger erstellen, per Code eine
 * Anfrage zum Verbinden stellen, vom Schulträger lösen. Verbunden ist die Schule erst, wenn
 * der Haupt-Trägeradmin die Anfrage annimmt. Arbeitet ein Träger-User gerade in einer
 * fremden Schule, zeigt die Karte nur den Stand; entscheiden kann das nur die Schule selbst.
 */
import { api } from "../api";
import { openModal, closeModal, modalBody } from "./modal";
import { confirmDialog } from "./confirm-dialog";
import { showToast } from "../utils/toast";
import { getCarrierView, switchCarrierView } from "../utils/carrier-view";
import type { SchoolCarrierStatus } from "../types";
import { esc } from "../utils/html";

const formatDay = (iso: string) => new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });

/**
 * Rendert die Karte in `box`. `onStatus` meldet den Stand, damit die Einstellungen
 * das Löschen der Schule sperren können, solange sie mit einem Träger verbunden ist.
 */
export async function renderSchoolCarrierCard(box: HTMLElement, onStatus?: (s: SchoolCarrierStatus) => void): Promise<void> {
  let status: SchoolCarrierStatus;
  try {
    status = await api.settings.carrier.status();
  } catch (err: any) {
    box.innerHTML = `<div class="card" style="padding:1.5rem"><h2 style="font-size:1rem;font-weight:600;margin-bottom:.5rem">Schulträger</h2><p class="text-muted" style="font-size:.875rem;margin:0">Stand konnte nicht geladen werden: ${esc(err.message)}</p></div>`;
    return;
  }
  onStatus?.(status);
  const readOnly = getCarrierView()?.mode === "schule";
  const c = status.carrier;
  const request = status.request;
  const rerender = () => renderSchoolCarrierCard(box, onStatus);

  let body: string;
  if (!c && request?.status === "offen") {
    body = `
      <p style="font-size:.875rem;color:var(--gray-600);margin:0 0 1rem">
        Deine Anfrage an den Schulträger <strong>${esc(request.carrierName)}</strong> vom ${formatDay(request.createdAt)} wartet auf Freigabe.
        Verbunden ist deine Schule erst, wenn der Haupt-Trägeradmin sie annimmt.
      </p>
      ${readOnly ? "" : `<button class="btn btn-secondary" id="sc-withdraw">Anfrage zurückziehen</button>`}`;
  } else if (!c) {
    const rejected = request?.status === "abgelehnt"
      ? `<div class="alert alert-error" style="margin-bottom:1rem;display:flex;justify-content:space-between;align-items:center;gap:1rem">
          <span>Der Schulträger <strong>${esc(request.carrierName)}</strong> hat deine Anfrage abgelehnt.</span>
          ${readOnly ? "" : `<button class="btn btn-sm" id="sc-dismiss">Hinweis ausblenden</button>`}
        </div>`
      : "";
    body = `
      ${rejected}
      <p style="font-size:.875rem;color:var(--gray-600);margin:0 0 1rem">
        Gehört deine Schule zu einem Schulträger mit mehreren Schulen? Mit einem Schulträger lassen sich die Schulen
        gemeinsam auswerten, und ausgewählte Benutzer können in mehreren Schulen arbeiten. Der Schulträger selbst ist kostenfrei,
        jede Schule bucht ihre Module weiterhin selbst.
      </p>
      ${readOnly ? "" : `<div style="display:flex;gap:.5rem;flex-wrap:wrap">
        <button class="btn btn-primary" id="sc-found">Schulträger erstellen</button>
        <button class="btn btn-secondary" id="sc-connect">Mit Schulträger verbinden</button>
      </div>`}`;
  } else if (c.isFoundingSchool) {
    body = `
      <p style="font-size:.875rem;color:var(--gray-600);margin:0 0 1rem">
        Deine Schule ist <strong>Stammschule</strong> des Schulträgers <strong>${esc(c.name)}</strong>
        (${c.schoolCount === 1 ? "1 Schule" : `${c.schoolCount} Schulen`}). Solange der Schulträger besteht, bleibt sie mit ihm verbunden
        und kann nicht gelöscht werden. Den Schulträger verwaltest du im Träger-Bereich.
      </p>
      <a class="btn btn-secondary" href="#/traeger">Zum Träger-Bereich</a>`;
  } else {
    body = `
      <p style="font-size:.875rem;color:var(--gray-600);margin:0 0 1rem">
        Deine Schule ist mit dem Schulträger <strong>${esc(c.name)}</strong> verbunden
        (${c.schoolCount === 1 ? "1 Schule" : `${c.schoolCount} Schulen`}). Vom Schulträger freigegebene Benutzer können in deiner Schule als Schul-Admin arbeiten.
      </p>
      ${readOnly ? "" : `<button class="btn btn-secondary" id="sc-leave">Vom Schulträger lösen</button>`}`;
  }

  box.innerHTML = `
    <div class="card" style="padding:1.5rem">
      <h2 style="font-size:1rem;font-weight:600;margin-bottom:1rem">Schulträger</h2>
      ${body}
    </div>`;

  box.querySelector("#sc-found")?.addEventListener("click", openFound);
  box.querySelector("#sc-connect")?.addEventListener("click", () => openConnect(rerender));
  box.querySelector("#sc-withdraw")?.addEventListener("click", async () => {
    const ok = await confirmDialog({
      title: "Anfrage zurückziehen",
      message: `Deine Anfrage an den Schulträger „${request!.carrierName}“ wird zurückgezogen. Du kannst später eine neue stellen.`,
      confirmLabel: "Zurückziehen",
    });
    if (!ok) return;
    try {
      await api.settings.carrier.withdrawRequest();
      showToast("Anfrage zurückgezogen");
      await rerender();
    } catch (err: any) { alert("Fehler: " + err.message); }
  });
  box.querySelector("#sc-dismiss")?.addEventListener("click", async () => {
    try { await api.settings.carrier.withdrawRequest(); await rerender(); }
    catch (err: any) { alert("Fehler: " + err.message); }
  });
  box.querySelector("#sc-leave")?.addEventListener("click", async () => {
    const ok = await confirmDialog({
      title: "Vom Schulträger lösen",
      message: `Deine Schule wird vom Schulträger „${c!.name}“ getrennt. Deine Daten bleiben erhalten.\n\nBenutzer deiner Schule verlieren ihren Zugang zum Schulträger, und der Schulträger kann deine Schule nicht mehr öffnen oder auswerten.`,
      confirmLabel: "Lösen",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.settings.carrier.leave();
      showToast("Vom Schulträger gelöst");
      await rerender();
    } catch (err: any) { alert("Fehler: " + err.message); }
  });
}

function openFound() {
  openModal("Schulträger erstellen", `
    <p style="font-size:.875rem;color:var(--gray-600);margin-top:0">
      Deine Schule wird Stammschule des neuen Schulträgers, und du wirst Haupt-Trägeradmin.
      Weitere Schulen stellen anschließend mit dem Code des Schulträgers eine Anfrage, die du annimmst.
    </p>
    <ul style="font-size:.875rem;color:var(--gray-600);padding-left:1.25rem">
      <li>Solange der Schulträger besteht, können weder deine Schule noch dein Benutzerkonto gelöscht werden.</li>
      <li>Löschen kannst den Schulträger nur du, im Träger-Bereich.</li>
    </ul>
    <div class="form-group"><label>Name des Schulträgers *</label><input id="sc-name" placeholder="z. B. Landkreis Musterstadt" /></div>
    <div id="sc-error" style="color:var(--danger,#dc2626);font-size:.875rem;min-height:1.2rem"></div>
    <div class="form-actions"><button class="btn btn-secondary" id="sc-cancel">Abbrechen</button><button class="btn btn-primary" id="sc-ok">Erstellen</button></div>`);
  const mb = modalBody();
  mb.querySelector("#sc-cancel")!.addEventListener("click", closeModal);
  mb.querySelector("#sc-ok")!.addEventListener("click", async () => {
    const name = (mb.querySelector("#sc-name") as HTMLInputElement).value.trim();
    const errorEl = mb.querySelector("#sc-error") as HTMLElement;
    if (!name) { errorEl.textContent = "Bitte einen Namen eingeben."; return; }
    const btn = mb.querySelector("#sc-ok") as HTMLButtonElement;
    btn.disabled = true;
    try {
      await api.settings.carrier.found(name);
      closeModal();
      // Neu starten, damit Menü und Rechte des Träger-Bereichs geladen werden
      switchCarrierView({ mode: "traeger" }, "#/traeger");
    } catch (err: any) {
      errorEl.textContent = err.message;
      btn.disabled = false;
    }
  });
}

function openConnect(onDone: () => Promise<void>) {
  openModal("Mit Schulträger verbinden", `
    <p style="font-size:.875rem;color:var(--gray-600);margin-top:0">
      Den Code bekommst du vom Schulträger. Er hat das Format TR-XXXX-XXXX-XXXX.
      Mit dem Code stellst du eine Anfrage; verbunden ist deine Schule erst, wenn der Haupt-Trägeradmin sie annimmt.
    </p>
    <div class="form-group"><label>Code *</label>
      <div style="display:flex;gap:.5rem"><input id="sc-code" placeholder="TR-XXXX-XXXX-XXXX" style="text-transform:uppercase" /><button class="btn btn-secondary" id="sc-check">Prüfen</button></div>
    </div>
    <div id="sc-preview"></div>
    <div id="sc-error" style="color:var(--danger,#dc2626);font-size:.875rem;min-height:1.2rem"></div>
    <div class="form-actions"><button class="btn btn-secondary" id="sc-cancel">Abbrechen</button><button class="btn btn-primary" id="sc-ok" disabled>Anfrage senden</button></div>`);
  const mb = modalBody();
  const codeEl = mb.querySelector("#sc-code") as HTMLInputElement;
  const okBtn = mb.querySelector("#sc-ok") as HTMLButtonElement;
  const errorEl = mb.querySelector("#sc-error") as HTMLElement;
  const preview = mb.querySelector("#sc-preview") as HTMLElement;

  const consentChecked = () => mb.querySelector<HTMLInputElement>("#sc-consent")?.checked === true;
  const updateOk = () => { okBtn.disabled = !consentChecked(); };
  codeEl.addEventListener("input", () => { preview.innerHTML = ""; errorEl.textContent = ""; updateOk(); });
  mb.querySelector("#sc-cancel")!.addEventListener("click", closeModal);
  mb.querySelector("#sc-check")!.addEventListener("click", async () => {
    errorEl.textContent = "";
    preview.innerHTML = "";
    try {
      const c = await api.settings.carrier.checkCode(codeEl.value.trim());
      const founding = [c.foundingSchool.name, c.foundingSchool.ort].filter(Boolean).join(", ");
      preview.innerHTML = `
        <div style="background:var(--gray-50);border:1px solid var(--gray-200);border-radius:.5rem;padding:1rem;margin-bottom:.75rem;font-size:.875rem">
          <div style="font-weight:600">${esc(c.name)}</div>
          <div class="text-muted">Stammschule: ${esc(founding)}</div>
          <div class="text-muted">${c.schoolCount === 1 ? "1 Schule" : `${c.schoolCount} Schulen`} verbunden</div>
        </div>
        <p style="font-size:.8125rem;color:var(--gray-600);margin:0 0 .75rem">
          Prüfe anhand der Stammschule, ob das der Schulträger ist, von dem du den Code bekommen hast.
        </p>
        <label style="display:flex;gap:.5rem;align-items:flex-start;font-size:.875rem">
          <input type="checkbox" id="sc-consent" style="margin-top:.2rem" />
          <span>Mir ist bewusst, dass vom Schulträger freigegebene Benutzer Admin-Zugriff auf alle Daten unserer Schule erhalten können,
          einschließlich der Daten der Schüler und der Abrechnung (Module buchen und kündigen). Der Schulträger sieht außerdem Auswertungen über unsere Schule.</span>
        </label>`;
      preview.querySelector("#sc-consent")!.addEventListener("change", updateOk);
    } catch (err: any) { errorEl.textContent = err.message; }
    updateOk();
  });
  okBtn.addEventListener("click", async () => {
    okBtn.disabled = true;
    try {
      const r = await api.settings.carrier.sendRequest(codeEl.value.trim(), consentChecked());
      closeModal();
      showToast(`Anfrage an ${r.name} gesendet`);
      await onDone();
    } catch (err: any) {
      errorEl.textContent = err.message;
      updateOk();
    }
  });
}
